import type { PageWithLayout, PageProps, IProductDetailModel } from "@types";
import type { Meta } from "@layouts/page";
import type { GetServerSideProps } from "next";
import Page from "@layouts/page";
import { getCookie, setContext } from "@utils/Cookies";
import { getLayout as getDefaultLayout } from "@layouts/default-layout";
import ProductDetail from "@templates/productDetail";
import ProductApi from "@api/productApi";
import Head from "next/head";
import { productObject } from "@utils/jsonLd";
import { useAppContext } from "@context/app";
import Loading from "assets/lottie/loading";
import { setCountryAndArea } from "@utils/checkDependencies";
import { parse } from 'next-useragent'
import { authApi, productApi } from "gate";
import { COOKIE_ACCESS_TOKEN_KEY } from "@utils/constants";
import useRequestImmutable from "@hooks/useRequestImmutable";
import useProductDetailQueryParams from "@hooks/useProductDetailQueryParams";
import { checkIfMaintenanceNeededOrFail } from "@utils/api";


interface Props extends PageProps {
  serverData?: IProductDetailModel
}

const ProductDetailPage: PageWithLayout<Props> = ({ serverData, appFallback }) => {
  const { currency_iso } = useAppContext()
  const { slug, source, source_slug, section, source_entity, section_entity, section_slug, q } = useProductDetailQueryParams();
  const { data: productData, error: productError } = useRequestImmutable(serverData ? null : productApi.getDetail(slug))

  if (!serverData && !productData && !productError) return <Loading />
  if (!serverData && !productData && productError) return <h1 className=" text-center my-20"> Product Not Found</h1>
  const finalData = serverData || productData?.data
  const meta: Meta = {
    title: finalData?.name ?? "Product Not Found",
    description: finalData?.description ?? appFallback?.website.description ?? "Dabdoob",
    image: finalData ? finalData.skus.find(s => s.id === finalData.default_sku_id)?.medias[0].url : undefined,
    keywords: appFallback?.website.keywords
  };
  return (
    <Page meta={meta}>
      <Head>
        {finalData ?
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(productObject(finalData, (currency_iso).toLocaleUpperCase())),
            }}
          /> : null}
      </Head>
      <ProductDetail data={finalData} slug={slug} source={source} q={q} sourceSlug={source_slug} section={section} sourceEntity={source_entity} sectionEntity={section_entity} sectionSlug={section_slug} />
    </Page>
  );
};

ProductDetailPage.translationFile = "translation";
ProductDetailPage.getLayout = (page, props) =>
  getDefaultLayout(page, { ...props }, {});

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  const { query } = ctx;

  const slug = typeof query.slug === "string" ? query.slug : "";
  //Hussein: Very important to set the context here to transfer auth token and locale info from client browser cookies to server
  setContext(ctx);
  const ssr = query.ssr ?? "";
  const ua = parse(ctx.req.headers['user-agent'] || "")

  const Cookies = getCookie();

  if (!Cookies.get(COOKIE_ACCESS_TOKEN_KEY)) {
    await authApi
      .deviceAuth()
      .fetch()
      .then((res) => {
        const token = res.data.access_token || "";
        Cookies.set(COOKIE_ACCESS_TOKEN_KEY, token, {}, 365 * 2);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  if ((ssr === "1" || ua.isBot)) {
    await setCountryAndArea(ctx.locale)
  }
  try {
    const data = await ProductApi.getDetail(slug).fetch()
    return {
      props: {
        serverData: data.data,
      },
    };
  } catch (error) {
    return checkIfMaintenanceNeededOrFail(error);
  }
};

export default ProductDetailPage;
